'use client';
import likesContract from '@bells/api/likes/frontend';
import { useAuth } from '@bells/core/auth';
import { useLanguage } from '@bells/core/i18n';
import { generateRestClient } from '@bells/core/rest/react';
import { baseLocale } from '@bells/i18n/runtime';
import { usePathname, useRouter } from 'next/navigation';
const systemRestClient = generateRestClient(likesContract);

import {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

export type LikesProps = {
  likes: number;
  hasLike: boolean;
  doLike: () => void;
  isLoading: boolean;
};

export const LikesContext = createContext<LikesProps>({
  likes: 0,
  hasLike: false,
  isLoading: false,
  doLike: () => {},
});

export const useLikes = (): LikesProps => {
  return useContext(LikesContext);
};

export default function LikeProvider({
  children,
  likes: initLikes,
  entity,
  entityId,
}: PropsWithChildren<{
  likes?: number;
  entity?: string;
  entityId?: string;
}>) {
  const { isAuthed } = useAuth();
  const { data, isLoading: initLoading } = systemRestClient.getLikes.useQuery({
    queryKey: ['likes', entity, entityId],
    queryData: {
      query: { entity: entity ?? '', entityId: entityId ?? '' },
    },
    select: (df) => df.body,
    enabled: entity == undefined || entityId == undefined,
  });

  const [likes, setLikes] = useState<number>(initLikes ?? 0);

  useEffect(() => {
    if (initLikes !== undefined) {
      setLikes(initLikes);
    }
  }, [initLikes]);
  const [hasLike, setHasLike] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const router = useRouter();
  const { language } = useLanguage();
  const pathParam = usePathname();
  useEffect(() => {
    setIsLoading(initLoading);
  }, [initLoading]);

  useEffect(() => {
    if (data) {
      setLikes(data.total);
      setHasLike(data.state);
    }
  }, [data]);

  const doLike = async () => {
    if (!entity || !entityId) {
      return;
    }
    if (isLoading) {
      return;
    }

    if (!isAuthed) {
      router.push(
        `${language == baseLocale ? `/auth/signin/customer` : `/${baseLocale}/auth/signin/customer`}?callbackUrl=${pathParam}`
      );
      return;
    } else {
      setIsLoading(true);
      const doLike = await systemRestClient.doLike.mutate({
        body: {
          entity,
          entityId,
        },
      });

      if (doLike.status == 200) {
        setHasLike(doLike.body.state);
        setLikes(doLike.body.total);
      }
      setIsLoading(false);
    }
  };

  const config = useMemo(() => {
    return { likes, hasLike, doLike, isLoading };
  }, [likes]);

  return (
    <LikesContext.Provider value={config}>{children}</LikesContext.Provider>
  );
}
